import { useTranslation } from "react-i18next";
import React, { useState, useMemo, useCallback } from "react";
import { useLocation } from "wouter";
import { useOrganization } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Layout } from "@/components/layout";
import { StatusBadge } from "@/components/ListPageTemplate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  Plus,
  ShoppingCart,
  Send,
  Trash2,
  Loader2,
  Truck,
} from "lucide-react";
import { STATUS_COLORS } from "@shared/schema";
import { useStatusLabel } from "@/lib/use-status-label";
import { useEnhancedFilteredList } from "@/hooks/use-fuzzy-search";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { formatDate } from "@/lib/date-utils";
import {
  useOrders,
  useSubmitOrder,
  useDeleteOrder,
  useOrderInquiry,
  OrderListResponse,
} from "@/hooks/use-orders";
import { OrderFulfillmentModal } from "@/components/orders/OrderFulfillmentModal";
import { useProductsLookup } from "@/hooks/use-products";
import { useToast } from "@/hooks/use-toast";
import { getHajocaHealth } from "@/requests/hajoca/get-health";
import { useProductEvents } from "@/hooks/use-product-events";

// Status colors now imported from centralized schema

// Component for the orders list view
const OrdersListView = React.memo(
  ({
    orders,
    isLoading,
    onCreateNew,
    onViewOrder,
    onSubmitOrder,
    onDeleteOrder,
    onTrackOrder,
    isFulfillmentTrackingEnabled,
    submittingOrderId,
    deletingOrderId,
    trackingOrderId,
    filterTabs,
    activeTab,
    onTabChange,
    searchQuery,
    onSearchChange,
    getKey,
  }: {
    orders: any[];
    isLoading: boolean;
    onCreateNew: () => void;
    onViewOrder: (order: any) => void;
    onSubmitOrder: (order: any) => void;
    onDeleteOrder: (order: any) => void;
    onTrackOrder: (order: any) => void;
    isFulfillmentTrackingEnabled?: boolean;
    submittingOrderId?: string | null;
    deletingOrderId?: string | null;
    trackingOrderId?: string | null;
    filterTabs?: any[];
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
    getKey?: (item: any, index: number) => string | number;
  }) => {
    const { t } = useTranslation();
    const formatOrderStatus = useStatusLabel("order");
    return (
      <div className="space-y-6">
        {/* Create button */}
        <div className="flex justify-end">
          <Button onClick={onCreateNew} size="lg" className="h-12 text-base">
            <Plus className="h-5 w-5 mr-2" />
            {t("pages.orders.newOrder")}
          </Button>
        </div>

        {/* Filter Tabs */}
        {filterTabs && filterTabs.length > 0 && (
          <div className="w-full">
            <Tabs
              value={activeTab}
              onValueChange={onTabChange}
              className="w-full"
            >
              <TabsList className="w-full justify-evenly">
                {filterTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="flex-1"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        )}

        {/* Search Input */}
        {searchQuery !== undefined && onSearchChange && (
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder={t("pages.orders.searchOrdersOrDescriptions")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 w-full h-12 text-base"
            />
          </div>
        )}

        {/* Cards Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-card p-6 rounded-lg border animate-pulse"
              >
                <div className="h-6 bg-muted rounded mb-3"></div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
                <div className="h-6 bg-muted rounded w-20 mb-2"></div>
                <div className="h-4 bg-muted rounded w-32 mb-4"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        ) : orders && orders.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {orders.map((order: any) => {
              return (
                <div
                  key={getKey ? getKey(order, 0) : order.uid}
                  data-testid="order-card"
                  className="bg-card p-6 rounded-lg border hover:shadow-md transition-shadow group"
                >
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2 gap-3">
                        <h3 className="font-semibold text-lg group-hover:text-primary transition-colors flex-1 min-w-0 truncate">
                          {order.title}
                        </h3>
                        <StatusBadge
                          status={order.status}
                          statusColors={STATUS_COLORS}
                          formatStatus={formatOrderStatus}
                        />
                      </div>

                      {order.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {order.description}
                        </p>
                      )}

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t("pages.orders.total")}
                          </span>
                          <span className="font-semibold text-lg">
                            {formatCentsAsDollars(
                              typeof order.amount === "number"
                                ? order.amount
                                : 0
                            )}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t("pages.orders.createdOn")}
                          </span>
                          <span className="text-sm">
                            {formatDate(order.createdAt)}
                          </span>
                        </div>

                        {order.createdByName && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                              {t("pages.orders.createdBy")}
                            </span>
                            <span className="text-sm">
                              {order.createdByName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      {order.orderStatus === "draft" ? (
                        <>
                          <Button
                            className="w-full h-12 text-base"
                            size="lg"
                            variant="default"
                            onClick={() => onSubmitOrder(order)}
                            disabled={submittingOrderId === order.uid}
                          >
                            {submittingOrderId === order.uid ? (
                              <Loader2 className="h-4 w-4 mr-2 text-white animate-spin" />
                            ) : (
                              <Send className="h-4 w-4 mr-2 text-white" />
                            )}
                            {submittingOrderId === order.uid
                              ? t("pages.orders.submitting")
                              : t("pages.orders.submitOrder")}
                          </Button>
                          <div className="flex gap-2">
                            <Button
                              className="flex-1 h-12 text-base"
                              size="lg"
                              variant="outline"
                              data-testid="view-edit-order-button"
                              onClick={() => onViewOrder(order)}
                            >
                              {t("pages.orders.viewEditOrder")}
                            </Button>
                            <Button
                              className="h-12 px-3"
                              size="lg"
                              variant="outline"
                              onClick={() => onDeleteOrder(order)}
                              disabled={deletingOrderId === order.uid}
                              title={t("pages.orders.deleteDraftOrder")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      ) : isFulfillmentTrackingEnabled ? (
                        <>
                          <Button
                            className="w-full h-12 text-base"
                            size="lg"
                            variant="default"
                            onClick={() => onTrackOrder(order)}
                            disabled={trackingOrderId === order.uid}
                          >
                            {trackingOrderId === order.uid ? (
                              <Loader2 className="h-4 w-4 mr-2 text-white animate-spin" />
                            ) : (
                              <Truck className="h-4 w-4 mr-2 text-white" />
                            )}
                            {trackingOrderId === order.uid
                              ? t("pages.orders.loading")
                              : t("pages.orders.trackOrder")}
                          </Button>
                          <Button
                            className="w-full h-12 text-base"
                            size="lg"
                            variant="outline"
                            data-testid="view-edit-order-button"
                            onClick={() => onViewOrder(order)}
                          >
                            {t("pages.orders.viewOrder")}
                          </Button>
                        </>
                      ) : (
                        <Button
                          className="w-full h-12 text-base"
                          size="lg"
                          variant="outline"
                          data-testid="view-edit-order-button"
                          onClick={() => onViewOrder(order)}
                        >
                          {t("pages.orders.viewOrder")}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">{t("pages.orders.noOrdersFound")}</h3>
              <p className="text-muted-foreground mb-6">
                {t("pages.orders.createYourFirstOrder")}
              </p>
              <Button onClick={onCreateNew} size="lg">
                <Plus className="h-5 w-5 mr-2" />
                {t("pages.orders.newOrder")}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default function Orders() {
  // Navigation
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  // UI state
  const [activeStatusTab, setActiveStatusTab] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<OrderListResponse | null>(null);
  const [submittingOrderId, setSubmittingOrderId] = useState<string | null>(
    null
  );
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(null);
  const [fulfillmentData, setFulfillmentData] = useState<any>(null);
  const [isFulfillmentModalOpen, setIsFulfillmentModalOpen] = useState(false);

  // Get orders data from API
  const { orders, isLoading } = useOrders();
  const submitOrderMutation = useSubmitOrder();
  const deleteOrderMutation = useDeleteOrder();
  const orderInquiryMutation = useOrderInquiry();
  const { organization } = useOrganization();
  const { toast } = useToast();
  const { data: hajocaHealth } = useQuery(getHajocaHealth());
  const { capture } = useProductEvents({ component: "Orders" });

  // Use PostHog's hook for proper async feature flag loading
  const isFulfillmentTrackingEnabled = useFeatureFlagEnabled("order-fulfillment-tracking") ?? false;

  // Extract product IDs from draft orders for current pricing
  const draftOrderProductIds = useMemo(() => {
    if (!orders) return [];
    const draftOrders = orders.filter((o) => o.orderStatus === "draft");
    const productIds: string[] = [];
    draftOrders.forEach((order) => {
      if (Array.isArray(order.products)) {
        order.products.forEach((p: any) => {
          if (p.productId && !productIds.includes(p.productId)) {
            productIds.push(p.productId);
          }
        });
      }
    });
    return productIds;
  }, [orders]);

  // Lookup current product data for draft orders
  const { products: draftProducts, isLoading: draftProductsLoading } =
    useProductsLookup(draftOrderProductIds, draftOrderProductIds.length > 0);

  // Create a map of current products for quick lookup
  const draftProductsMap = useMemo(() => {
    const map = new Map();
    draftProducts.forEach((p) => map.set(p.uid, p));
    return map;
  }, [draftProducts]);

  // Filter tabs configuration for orders - using actual enum values
  const orderFilterTabs = useMemo(
    () => [
      { label: t("pages.orders.all"), value: "all" },
      { label: t("pages.orders.draft"), value: "draft" },
      { label: t("pages.orders.submitted"), value: "submitted" },
    ],
    []
  );

  const handleCreateNew = useCallback(() => {
    setLocation("/orders/new");
  }, [setLocation]);

  const handleViewOrder = useCallback((order: OrderListResponse) => {
    const isDraft = order.orderStatus === "draft";
    if (isDraft) {
      capture({ event: "edit_draft_order", context: { orderId: order.uid } });
    }
    setLocation(`/orders/edit/${order.uid}${isDraft ? "" : "?mode=view"}`);
  }, [capture, setLocation]);

  const handleSubmitOrder = useCallback(
    async (order: OrderListResponse) => {
      const productCount = Array.isArray(order.products) ? order.products.length : 0;
      capture({
        event: "order_submit_started",
        context: { productCount, mode: "list" },
      });
      try {
        const hajocaCustomerId = organization?.publicMetadata
          ?.hajocaCustomerId as string;

        if (!hajocaCustomerId) {
          console.error("No Hajoca customer ID found in organization metadata");
          return;
        }

        setSubmittingOrderId(order.uid);
        await submitOrderMutation.mutateAsync({
          orderId: order.uid,
          hajocaCustomerId,
        });
      } catch (error: any) {
        capture({
          event: "order_submit_failed",
          context: {
            productCount,
            mode: "list",
            error: error?.message ?? "submission_failed",
          },
        });
        console.error("Failed to submit order:", error);
        toast({
          title: t("pages.orders.orderSubmissionUnavailable"),
          description:
            t("pages.orders.orderSubmissionIsCurrently"),
          variant: "destructive",
        });
      } finally {
        setSubmittingOrderId(null);
      }
    },
    [capture, submitOrderMutation, organization, hajocaHealth, toast]
  );

  const handleDeleteOrder = useCallback(
    async (order: OrderListResponse) => {
      if (
        window.confirm(
          `Are you sure you want to delete "${order.title}"? This action cannot be undone.`
        )
      ) {
        try {
          setDeletingOrderId(order.uid);
          await deleteOrderMutation.mutateAsync(order.uid);
        } catch (error) {
          console.error("Failed to delete order:", error);
        } finally {
          setDeletingOrderId(null);
        }
      }
    },
    [deleteOrderMutation]
  );

  const handleTrackOrder = useCallback(
    async (order: OrderListResponse) => {
      if (!isFulfillmentTrackingEnabled) {
        return;
      }

      try {
        const hajocaCustomerId = organization?.publicMetadata
          ?.hajocaCustomerId as string;

        if (!hajocaCustomerId) {
          console.error("No Hajoca customer ID found in organization metadata");
          toast({
            title: t("pages.orders.cannotTrackOrder"),
            description: t("pages.orders.hajocaCustomerIdNot"),
            variant: "destructive",
          });
          return;
        }

        setTrackingOrderId(order.uid);
        const result = await orderInquiryMutation.mutateAsync({
          orderId: order.uid,
          shipToId: hajocaCustomerId,
        });
        setFulfillmentData(result);
        setSelectedOrder(order);
        setIsFulfillmentModalOpen(true);
      } catch (error: any) {
        console.error("Failed to query order status:", error);

        const errorMessage = error?.response?.data?.error ||
                           error?.message ||
                           "Failed to retrieve order status";

        toast({
          title: t("pages.orders.failedToRetrieveOrder"),
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setTrackingOrderId(null);
      }
    },
    [isFulfillmentTrackingEnabled, orderInquiryMutation, organization, toast]
  );

  const handleCloseFulfillmentModal = useCallback(() => {
    setIsFulfillmentModalOpen(false);
    setFulfillmentData(null);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveStatusTab(tab);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getOrderKey = useCallback((order: any, index: number) => {
    return order.uid || `order-${index}`;
  }, []);

  // Enhanced orders for display with current pricing for drafts
  const ordersWithDisplayFields = useMemo(() => {
    if (!orders) return [];

    return orders
      .map((order) => {
        let displayAmount = order.totalOrderCost || 0;

        // For draft orders: recalculate total with current prices
        if (
          order.orderStatus === "draft" &&
          Array.isArray(order.products) &&
          !draftProductsLoading
        ) {
          let recalculatedTotal = 0;
          order.products.forEach((orderProduct: any) => {
            const currentProduct = draftProductsMap.get(orderProduct.productId);
            const unitPrice =
              currentProduct?.currentPrice || orderProduct.unitPrice || 0;
            recalculatedTotal += unitPrice * (orderProduct.quantity || 0);
          });
          displayAmount = recalculatedTotal;
        }

        return {
          ...order,
          // Map API fields to display fields for compatibility
          title: order.title || "Order",
          description: order.description || "Order",
          // For draft orders: use recalculated amount with current prices
          // For submitted orders: use saved historical total
          amount: displayAmount,
          status: order.orderStatus,
          createdAt: order.createdAt,
        };
      })
      .sort((a, b) => {
        // Sort by creation time, newest first (same as quotes)
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      });
  }, [orders, draftProductsMap, draftProductsLoading]);

  // Filtered and searched orders using enhanced fuzzy search
  const filteredOrders = useEnhancedFilteredList({
    items: ordersWithDisplayFields,
    activeTab: activeStatusTab,
    searchQuery,
    statusKey: "status",
    searchKeys: ["title", "description"],
    customSearchFn: (order: any, query: string) => {
      // Fallback exact matching if fuzzy search fails
      return (
        order.title?.toLowerCase().includes(query) ||
        order.description?.toLowerCase().includes(query)
      );
    },
  });

  return (
    <Layout headerContent={<span className="font-semibold">{t("common.orders")}</span>}>
      <div className="space-y-6">
        <OrdersListView
          orders={filteredOrders}
          isLoading={isLoading}
          onCreateNew={handleCreateNew}
          onViewOrder={handleViewOrder}
          onSubmitOrder={handleSubmitOrder}
          onDeleteOrder={handleDeleteOrder}
          onTrackOrder={handleTrackOrder}
          isFulfillmentTrackingEnabled={isFulfillmentTrackingEnabled}
          submittingOrderId={submittingOrderId}
          deletingOrderId={deletingOrderId}
          trackingOrderId={trackingOrderId}
          filterTabs={orderFilterTabs}
          activeTab={activeStatusTab}
          onTabChange={handleTabChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          getKey={getOrderKey}
        />

        {isFulfillmentTrackingEnabled && (
          <OrderFulfillmentModal
            isOpen={isFulfillmentModalOpen}
            onClose={handleCloseFulfillmentModal}
            fulfillmentData={fulfillmentData}
            poNumber={selectedOrder?.title || t("pages.orders.order")}
            hajocaOrderId={selectedOrder?.hajocaOrderId || undefined}
          />
        )}
      </div>
    </Layout>
  );
}
